import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export function usePermissions() {
  const { user } = useAuth();

  const hasPermission = useCallback(
    (module: string, action: string, requiredLevel: number = 2): boolean => {
      // If no permissions loaded (backend not deployed or no roles assigned yet),
      // allow everything so the UI doesn't break
      if (!user?.permissions || user.permissions.length === 0) return true;
      return user.permissions.some(
        (p) => p.module === module && p.action === action && p.accessLevel >= requiredLevel
      );
    },
    [user?.permissions]
  );

  // canView requires at least ReadOnly (level 1)
  const canView = useCallback((module: string) => hasPermission(module, 'view', 1), [hasPermission]);

  // Strict, deny-by-default visibility for opt-in modules (e.g. billing):
  // a module is hidden unless the user has been explicitly granted
  // `<module>.view`. Unlike canView, this does NOT fall back to "allow all"
  // when the user has no permissions assigned, and — deliberately — platform
  // admins do NOT bypass it: even an Admin must be granted `<module>.view`
  // before the module appears. Admins can always grant it to themselves via
  // the Roles/Permissions page (which is NOT a strict module).
  const canViewStrict = useCallback(
    (module: string): boolean => {
      return (user?.permissions ?? []).some(
        (p) => p.module === module && p.action === 'view' && p.accessLevel >= 1
      );
    },
    [user?.permissions]
  );
  // Mutation actions require FullAccess (level 2)
  const canCreate = useCallback((module: string) => hasPermission(module, 'create', 2), [hasPermission]);
  const canEdit = useCallback((module: string) => hasPermission(module, 'edit', 2), [hasPermission]);
  const canDelete = useCallback((module: string) => hasPermission(module, 'delete', 2), [hasPermission]);
  const canExport = useCallback((module: string) => hasPermission(module, 'export', 2), [hasPermission]);

  return { hasPermission, canView, canViewStrict, canCreate, canEdit, canDelete, canExport };
}
